import React, { useState, useEffect } from 'react';
import { ArrowDown, Download, Eye, Mail } from 'lucide-react';
import CodeLaptop3D from './CodeLaptop3D';

const Hero = () => {
  const [currentRole, setCurrentRole] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const roles = [
    'Data Scientist',
    'Generative AI Engineer',
    'Machine Learning Developer',
    'RAG & Multi-Agent Systems Builder'
  ];

  useEffect(() => {
    setIsLoaded(true);
    const interval = setInterval(() => {
      setCurrentRole((prev) => (prev + 1) % roles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        background: 'var(--bg-primary)',
        paddingTop: '100px',
        paddingBottom: '50px'
      }}
    >
      {/* Background gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 30% 50%, rgba(0, 255, 209, 0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 50%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)'
        }}
      />

      {/* Main Container - Two Column Layout */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
          
          {/* Left Side - Content */}
          <div 
            className="flex-1 text-center lg:text-left max-w-2xl"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateX(0)' : 'translateX(-30px)',
              transition: 'all 0.6s ease-out'
            }}
          >
            {/* Available Badge */}
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full"
              style={{
                background: 'rgba(0, 255, 209, 0.1)',
                border: '1px solid rgba(0, 255, 209, 0.3)'
              }}
            >
              <span 
                className="w-2 h-2 rounded-full"
                style={{ background: '#00ffd1', animation: 'pulse 2s infinite' }}
              />
              <span style={{ color: '#00ffd1', fontSize: '14px' }}>
                Available for Projects
              </span>
            </div>

            {/* Profile Image */}
            <div className="mb-6 flex justify-center lg:justify-start">
              <div
                className="rounded-full overflow-hidden"
                style={{
                  width: '140px',
                  height: '140px',
                  border: '3px solid #00ffd1',
                  boxShadow: '0 0 40px rgba(0, 255, 209, 0.2)'
                }}
              >
                <img
                  src="/tushar-profile.jpeg"
                  alt="Tushar Singh"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Name */}
            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-3"
              style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
            >
              Tushar Singh
            </h1>

            {/* Animated Role */}
            <div className="h-10 mb-4 flex items-center justify-center lg:justify-start overflow-hidden">
              <p
                key={currentRole}
                className="text-xl md:text-2xl font-medium"
                style={{
                  color: '#00ffd1',
                  animation: 'fadeIn 0.4s ease-out'
                }}
              >
                {roles[currentRole]}
              </p>
            </div>

            {/* Description */}
            <p
              className="text-base md:text-lg mb-8 leading-relaxed"
              style={{ color: 'var(--text-secondary)' }}
            >
              Data Scientist with hands-on experience in machine learning and intelligent
              applications, including <span style={{ color: '#00ffd1' }}>RAG systems</span> and
              multi-agent workflows. CAMEL-AI Ambassador with strong Python and problem-solving
              foundations.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
              <a
                href="#projects"
                className="px-6 py-3 font-medium inline-flex items-center gap-2 rounded-lg transition-all duration-300"
                style={{
                  background: '#00ffd1',
                  color: '#000000'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 255, 209, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Eye size={18} />
                View Projects
              </a>

              <a
                href="https://drive.google.com/file/d/1yNuShH9rW9-SY_58GVaX9j1Pr-jugMqV/view?usp=sharing"
                download="Tushar_Singh_Resume.pdf"
                className="px-6 py-3 font-medium inline-flex items-center gap-2 rounded-lg transition-all duration-300"
                style={{
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-medium)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00ffd1';
                  e.currentTarget.style.color = '#00ffd1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-medium)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
              >
                <Download size={18} />
                Resume
              </a>

              <a
                href="#contact"
                className="px-6 py-3 font-medium inline-flex items-center gap-2 rounded-lg transition-all duration-300"
                style={{
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-medium)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#00ffd1';
                  e.currentTarget.style.color = '#00ffd1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-medium)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
              >
                <Mail size={18} />
                Contact
              </a>
            </div>

            {/* Stats */}
            <div className="flex justify-center lg:justify-start gap-8 mt-10">
              {[
                { value: '20+', label: 'Agent Systems' },
                { value: '200+', label: 'DSA Solved' },
                { value: '2024+', label: 'AI Journey' }
              ].map((stat, i) => (
                <div key={i} className="text-center lg:text-left">
                  <div className="text-2xl font-bold" style={{ color: '#00ffd1' }}>
                    {stat.value}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side - 3D MacBook */}
          <div 
            className="flex-1 flex justify-center lg:justify-end"
            style={{
              opacity: isLoaded ? 1 : 0,
              transform: isLoaded ? 'translateX(0)' : 'translateX(30px)',
              transition: 'all 0.6s ease-out 0.2s'
            }}
          >
            <div 
              className="relative"
              style={{
                transform: 'scale(0.85)',
                filter: 'drop-shadow(0 0 50px rgba(0, 255, 209, 0.15))'
              }}
            >
              <CodeLaptop3D />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <ArrowDown
          size={24}
          style={{ color: '#00ffd1', animation: 'bounce 2s infinite' }}
        />
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }
      `}</style>
    </section>
  );
};

export default Hero;




































